import React, { useState, useRef } from 'react';
import { string, shape, func, bool } from 'prop-types';
import { noop } from 'lodash';
import { VariationListings, Variation, TARGET_TYPES, hasCurvedText } from '@fiverr-private/variations_display';
import pathfinder from '@fiverr-private/pathfinder';
import { URI } from '@fiverr-private/futile';
import { useImpressions } from '../../../../shared/hooks';
import { variationClicked, inspirationFieldsChanged, getIdeaPageName } from '../lib/bi-events';
import { INSPIRATION } from '../propTypes/inspiration';
import { INNER_CLASSES, TAG_PAGE_SOURCE } from '../constants';
import { getRowItemClassNameByIndex } from './helpers';
import VariationsToolBar from './VariationsToolBar';
import MakeNewLogo from './MakeNewLogo';
import { INSPIRATION_ENGINE_TYPE, VARIATION_CARD_CLASSNAME, VARIATION_CARD_DOM_ID_ATTRIBUTE } from './constants';
import './style.scss';

const Inspiration = ({
    title,
    tagType,
    tagName,
    tagId,
    brandName: initialBrandName,
    slogan: initialSlogan,
    pageCtxId,
    refCtxId,
    uid,
    onChange = noop,
    inspiration: { ctaTitle, ctaSubtitle, variations },
    isTouch,
    isServerEntry,
}) => {
    const variationsRef = useRef(null);
    const [{ brandName, slogan }, setTextFields] = useState({
        brandName: initialBrandName,
        slogan: initialSlogan,
    });
    const getRowItemClassName = (variation) => {
        const index = variations.findIndex(({ id }) => variation.id === id);
        return `col-6-xs col-4-sm col-3-md ${getRowItemClassNameByIndex(variations.length, index)}`;
    };

    const updateFields = (changes, { name, label } = {}) => {
        onChange(changes);
        setTextFields(changes);

        inspirationFieldsChanged({
            ...changes,
            pageTagType: tagType,
            pageTagId: tagId,
            elementName: name,
            elementText: label,
            pageCtxId,
        });
    };

    const trackVariationClicked = (variation) =>
        variationClicked({
            pageTagType: tagType,
            pageTagId: tagId,
            projectId: variation.projectId,
            variationId: variation.id,
            pageCtxId,
        });

    const getLinkUrl = (variationId, projectId) =>
        new URI(
            pathfinder('logo_maker_choose_project_variation_page', {
                project_id: projectId,
            })
        )
            .addParams({
                variation_id: variationId,
                brand_name: brandName,
                slogan,
                source: TAG_PAGE_SOURCE,
                ref_ctx_id: pageCtxId,
            })
            .toString();

    useImpressions({
        type: 'logo_card_is_shown',
        DOMSelector: `.${VARIATION_CARD_CLASSNAME}`,
        DOMAttribute: VARIATION_CARD_DOM_ID_ATTRIBUTE,
        variations,
        impressionEnrichment: {
            briefId: null,
            uid,
            gig_impression: [
                {
                    listings: { page_ctx_id: pageCtxId },
                    type: INSPIRATION_ENGINE_TYPE,
                },
            ],
            page: {
                name: getIdeaPageName(tagType, tagId),
                referrer: { ctx_id: refCtxId },
            },
        },
        containerRef: variationsRef,
    });

    return (
        <div className="inspiration">
            <div className={INNER_CLASSES} ref={variationsRef}>
                <h2>{title}</h2>
                <VariationsToolBar brandName={brandName} slogan={slogan} onChange={updateFields} />
                <VariationListings
                    variations={variations}
                    wrapperClass="grid-12 variations-grid"
                    brandName={brandName}
                    slogan={slogan}
                    observeChanges={true}
                    renderChild={(variation) => (
                        <div
                            key={variation.id}
                            className={getRowItemClassName(variation)}
                            {...{
                                [VARIATION_CARD_DOM_ID_ATTRIBUTE]: variation.id,
                            }}
                        >
                            <div className="inspiration-card-wrapper">
                                <a
                                    href={getLinkUrl(variation.id, variation.projectId)}
                                    target={isTouch ? TARGET_TYPES.SELF : TARGET_TYPES.BLANK}
                                    rel="noopener nofollow noreferrer"
                                    onClick={() => trackVariationClicked(variation)}
                                    title={`${tagName} logo`}
                                >
                                    {isServerEntry && variation.staticImgUrl ? (
                                        <img src={variation.staticImgUrl} alt={`${tagName} logo`} />
                                    ) : (
                                        <Variation
                                            {...variation}
                                            withWatermark={true}
                                            applyVerticalChanges={!hasCurvedText(variation)}
                                        />
                                    )}
                                </a>
                            </div>
                        </div>
                    )}
                />

                <MakeNewLogo
                    ctaTitle={ctaTitle}
                    ctaSubtitle={ctaSubtitle}
                    brandName={brandName}
                    slogan={slogan}
                    tagId={tagId}
                    tagType={tagType}
                />
            </div>
        </div>
    );
};

Inspiration.propTypes = {
    onChange: func,
    brandName: string,
    slogan: string,
    title: string,
    tagType: string,
    tagName: string,
    tagId: string,
    pageCtxId: string,
    refCtxId: string,
    uid: string,
    inspiration: shape(INSPIRATION),
    isTouch: bool,
    isServerEntry: bool,
};

export default Inspiration;
